import { Skeleton } from "@mantine/core";
import { EncryptStorage } from "encrypt-storage";
import { Navigate, useLocation } from "react-router";

import { env } from "@/config/env";
import { paths } from "@/config/paths";
import { useUser } from "@/services/auth";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { AuthResponse } from "@/types/auth";
import type { FC, PropsWithChildren } from "react";

const key = `${env.STORAGE_PREFIX}token`;

const storage = new EncryptStorage(env.STORAGE_SECRET_KEY, {
  stateManagementUse: false,
  storageType: "localStorage",
});

export function removeAccessToken() {
  storage.removeItem(key);
}

export function getAccessToken() {
  return storage.getItem<AuthResponse>(key);
}

export function hasAccessToken() {
  return !!getAccessToken();
}

export function setAccessToken(token: AuthResponse) {
  storage.setItem(key, token);
}

/** Refresh the token this many ms before it expires. */
const REFRESH_THRESHOLD_MS = 8 * 60 * 60 * 1000; // 8 hours

export function isTokenExpiringSoon(): boolean {
  const token = getAccessToken();
  if (!token?.access_token_expires) return false;
  const expiresAt = new Date(token.access_token_expires).getTime();
  return Date.now() >= expiresAt - REFRESH_THRESHOLD_MS;
}

/**
 * Calls POST /auth/refresh/ using the current bearer token and stores the
 * refreshed token. Uses raw fetch to avoid a circular dependency with the
 * Axios api-client.
 */
export async function refreshAccessToken(): Promise<void> {
  const current = getAccessToken();
  if (!current?.access_token) return;

  const response = await fetch(`${env.API_URL}/auth/refresh/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${current.access_token}`,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    removeAccessToken();
    throw new Error("Token refresh failed");
  }

  const data: AuthResponse = await response.json();
  setAccessToken({
    access_token: data.access_token,
    access_token_expires: data.access_token_expires,
    hostname: data.hostname ?? current.hostname,
  });
}

export const ProtectedRoute: FC<PropsWithChildren> = ({ children }) => {
  const user = useUser();
  const location = useLocation();
  const { isLoading: isPlansLoading } = usePlansUser();

  if (user.isFetching || user.isLoading || user.isPending || isPlansLoading) {
    return <Skeleton h="100vh" />;
  }

  if (!getAccessToken() || !user.data) {
    return <Navigate to={paths.auth.login.getHref(location.pathname)} replace />;
  }

  return children;
};

export const ProtectedOnboardingRoute: FC<PropsWithChildren> = ({ children }) => {
  const user = useUser();
  const location = useLocation();
  const {
    /* data: plans, */
    isLoading: isPlansLoading,
  } = usePlansUser();

  if (user.isFetching || user.isLoading || user.isPending || isPlansLoading) {
    return <Skeleton h="100vh" />;
  }

  if (!getAccessToken() || !user.data) {
    return <Navigate to={paths.auth.login.getHref(location.pathname)} replace />;
  }

  /* const u = user.data as any; */

  /*   const onboardingCompleted =
    u?.additional_info?.onboarding_completed === true ||
    (u?.additional_info?.onboarding && Object.keys(u.additional_info.onboarding).length > 0);

  const planSelected = !!plans?.name; */

  // If onboarding not completed or the user has no selected plan, redirect to /plans
  /*   if (!onboardingCompleted || !planSelected) {
    return <Navigate to={paths.plans.root.getHref()} replace />;
  } */

  return children;
};
